<template>
  <div class="hcl-tiles-tile tile-edit" v-if="!render">
    <div class="tile" ref="tileRef" :style="calcStyleTileEditor(props.tile)">
      <div class="content" ref="contentRef">
        <component v-if="!props.tile.chooser" :is="componentName" :tile="props.tile" />
      </div>
      <div class="close-action" @click.prevent.stop="close" @mousedown.prevent.stop></div>
      <div class="drag-action" @mousedown.left.prevent.stop="mousedown('drag', $event)">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
          <path fill="none" d="M0 0h24v24H0z" />
          <path
            d="M11 11V5.828L9.172 7.657 7.757 6.243 12 2l4.243 4.243-1.415 1.414L13 5.828V11h5.172l-1.829-1.828 1.414-1.415L22 12l-4.243 4.243-1.414-1.415L18.172 13H13v5.172l1.828-1.829 1.415 1.414L12 22l-4.243-4.243 1.415-1.414L11 18.172V13H5.828l1.829 1.828-1.414 1.415L2 12l4.243-4.243 1.414 1.415L5.828 11z" />
        </svg>
      </div>
      <!--<div class="resize-action" @mousedown.left.prevent.stop="mousedown('resize', $event)"></div>-->
    </div>
  </div>
  <div v-else class="hcl-tiles-tile tile-render" :class="calcClassTileRender(props.tile)"
    :style="calcStyleTileRender(props.tile)" :data-tile-dimension-row-height="props.dimensions.rows.height"
    :data-tile-dimension-row-unit="props.dimensions.rows.unit">
    <component v-if="!props.tile.chooser" :is="componentName" :tile="props.tile"
      v-on:hcl-tiles-tile-change-height="onTilesTileChangeHeight" v-on:hcl-form-input-enter-key="onFormInputEnterKey" />
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'

import { DefinitionTile, DefinitionTilesDimensions } from '@/components/types'

import HclTilesTileContentFormElement from '@/components/TilesTileContentFormElement/TilesTileContentFormElement.vue'
import HclTilesTileContentEmpty from '@/components/TilesTileContentEmpty/TilesTileContentEmpty.vue'

interface Props {
  /**
   * Definition as an JS object.
   */
  tile: DefinitionTile
  dimensions: DefinitionTilesDimensions
  render: any
}

const props = withDefaults(defineProps<Props>(), {
  tile: undefined,
  dimensions: undefined,
  render: undefined
})

const tileRef = ref()
const contentRef = ref()

const calcStyleTileEditor = (tile: DefinitionTile) => {
  const top0 = tile.row * props.dimensions.rows.height + props.dimensions.rows.unit
  const left0 = tile.column * props.dimensions.cols.width + props.dimensions.cols.unit
  const height0 = tile.height * props.dimensions.rows.height + props.dimensions.rows.unit
  const width0 = tile.width * props.dimensions.cols.width + props.dimensions.cols.unit

  const style = { top: top0, left: left0, height: height0, width: width0 }

  return style
}

const calcClassTileRender = (tile: DefinitionTile) => {
  return `col-12 col-md-${tile.width}`
}

const calcStyleTileRender = (tile: DefinitionTile) => {
  const height = tile.height * props.dimensions.rows.height + props.dimensions.rows.unit

  const style = { 'min-height': height }

  return style
}

const emit = defineEmits(['drag', 'resize', 'tile-chooser-change', 'hcl-tiles-tile-change-height', 'hcl-form-input-enter-key'])

const mousedown = (name: 'drag' | 'resize', e) => {
  if (!document.defaultView) {
    return
  }

  const outer = document.defaultView.getComputedStyle(tileRef.value)
  const inner = document.defaultView.getComputedStyle(contentRef.value)

  emit(name, {
    tile: props.tile,
    event: e,
    initialPos: {
      left:
        parseFloat(outer.getPropertyValue('left')) +
        parseFloat(outer.getPropertyValue('padding-left')) +
        parseFloat(outer.getPropertyValue('border-left-width')),
      top:
        parseFloat(outer.getPropertyValue('top')) +
        parseFloat(outer.getPropertyValue('padding-top')) +
        parseFloat(outer.getPropertyValue('border-top-width')),
      width: parseFloat(inner.getPropertyValue('width')),
      height: parseFloat(inner.getPropertyValue('height'))
    }
  })
}

const close = () => {
  emit('tile-chooser-change', { id: props.tile.id, chooser: true })
}

const componentName = computed(() => {
  if (props.tile.entity.type === 'FormElement') {
    return HclTilesTileContentFormElement
  }

  return HclTilesTileContentEmpty
})

const onTilesTileChangeHeight = (event) => {
  emit('hcl-tiles-tile-change-height', event)
}

const onFormInputEnterKey = (event) => {
  emit('hcl-form-input-enter-key', event)
}
</script>

<style lang="scss">
$action-border: 1px solid #ccc;
$action-background-color: #fff;
$action-background-color-hover: black;
$action-icon-color: #666;
$action-icon-color-hover: white;

.tile-render {
  padding: 4px 4px 4px 4px !important;

  .tile {
    position: fixed;
  }
}

.tile {
  padding: 2px;
  position: absolute;

  .content,
  .close-action,
  .drag-action {
    display: flex;
    flex-direction: column;
  }

  .content {
    height: 100%;
    border: 1px solid #ccc;
    background: #f4f4f4;
    padding: 0px 1px 0px 1px;
  }

  .close-action {
    position: absolute;
    top: 2px;
    height: 22px;
    right: 2px;
    width: 22px;
    background-color: $action-background-color;
    border: $action-border;
    border-bottom-left-radius: 5px;

    &::before,
    &::after {
      content: '';
      position: absolute;
      top: 50%;
      height: 2px;
      right: 2px;
      left: 2px;
      margin-top: -1px;
      background-color: $action-icon-color;
    }

    &::before {
      transform: rotate(45deg);
    }

    &::after {
      transform: rotate(-45deg);
    }

    &:hover {
      background-color: $action-background-color-hover;

      &::before,
      &::after {
        border: 1px solid $action-icon-color-hover;
      }
    }
  }

  .drag-action {
    position: absolute;
    top: 2px;
    height: 22px;
    right: 35px;
    width: 25px;
    background-color: $action-background-color;
    border: $action-border;
    border-bottom-left-radius: 5px;
    border-bottom-right-radius: 5px;
    cursor: move;

    svg {
      fill: $action-icon-color;
      height: 23px;
      width: 23px;
    }

    &:hover {
      background-color: $action-background-color-hover;

      svg {
        fill: $action-icon-color-hover;
      }
    }
  }

  .resize-action {
    position: absolute;
    right: 2px;
    bottom: 2px;
    width: 8px;
    height: 8px;
    cursor: nwse-resize;
    border: $action-border;
    background: $action-background-color;

    &:hover {
      background-color: $action-background-color-hover;
    }
  }

  &.interact {
    transition-property: top, left, width, height;
    transition-duration: 0.4s;
  }
}
</style>
